import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { AnalysisRunSchema } from '../analysis/schemas'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** Attach matter documents, including uploads still processing to the existing table analysis. */
export async function postAddTableDocuments(matterId: string, tableId: string, documentIds: string[]) {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/matter/${matterId}/tables/${tableId}/documents`
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ document_ids: documentIds }),
  })
  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    if (response.status >= 500) Sentry.captureException(new Error('Table analysis request failed'), { extra: getResponseDebugContext(response, url) })
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    const approvedMessages = [
      'Matter not found',
      'Table resource not found.',
      'Table creator access required',
      'Select available documents in this matter.',
      'Select at least one document in this matter.',
    ]
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' && approvedMessages.includes(detail)
        ? detail
        : 'Unable to add documents to the table.'
    )
  }
  const parsed = AnalysisRunSchema.safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid analysis response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The analysis response could not be read. Please retry.')
  }
  return parsed.data
}
